import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import moment from 'moment';
import { utils, write } from 'xlsx';
import { saveAs } from 'file-saver';
import { toast, ToastContainer } from 'react-toastify';
import useHandleErrors from '../../utils/user.utils';

const TimesheetReport = () => {
    const [timesheets, setTimesheets] = useState([]);
    const [filter, setFilter] = useState('currentMonth');
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredGroupedTimesheets, setFilteredGroupedTimesheets] = useState({});
    const handleErrors = useHandleErrors();
    const hasFetchedRef = useRef(false);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [selectedTimesheet, setSelectedTimesheet] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const token = localStorage.getItem("token");
                const path = `/viewTimesheet`;
                const URL = process.env.REACT_APP_BE_URL + path;
                const response = await axios.get(URL, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                })
                if (response.status === 200) {
                    const data = response.data.listTimesheetDetails.flatMap(emp =>
                        emp.timesheetSummary.map(summary => ({
                            ...summary,
                            employeeId: emp.employeeId,
                            employeeName: emp.employeeName
                        }))
                    );
                    setTimesheets(data);
                } else {
                    handleErrors(response);
                }
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 404) {
                        setTimesheets([]);
                        setFilteredGroupedTimesheets({});
                    } else {
                        handleErrors(error.response);
                    }
                } else {
                    toast.error("Error fetching timesheet details.", {
                        position: toast.POSITION.TOP_CENTER,
                    });
                }
            } finally {
                setLoading(false);
            }
        };
        if (!hasFetchedRef.current) {
            fetchData();
            hasFetchedRef.current = true;
        }
    }, [handleErrors]);

    const getUniqueCommentsByDate = (timeSheetHours) => {
        const commentMap = new Map();
        timeSheetHours.forEach(day => {
            day.projectHours.forEach(ph => {
                if (ph.userComments) {
                    commentMap.set(day.weekDate, ph.userComments);
                }
            });
        });
        return Array.from(commentMap.entries()).map(([date, comment]) => ({
            date,
            comment
        })).sort((a, b) => new Date(a.date) - new Date(b.date));
    };

    // Calculate hours for a specific month from a timesheet
    const calculateHoursForMonth = (timesheet, targetMonthYear) => {
        let totalHours = 0;
        
        timesheet.timeSheetHours.forEach(day => {
            const dayMonthYear = moment(day.weekDate, 'YYYY-MM-DD').format('MMMM YYYY');
            if (dayMonthYear === targetMonthYear) {
                const dayTotal = day.projectHours.reduce((sum, project) => sum + project.hours, 0);
                totalHours += dayTotal;
            }
        });
        
        return totalHours;
    };

    const getFilteredTimesheets = () => {
        return timesheets;
    };

    const groupByMonth = (timesheets) => {
        // Group timesheets by month and year
        const grouped = timesheets.reduce((acc, timesheet) => {
            // Get all unique months in this timesheet
            const monthsInTimesheet = new Set();
            
            timesheet.timeSheetHours.forEach(day => {
                const monthYear = moment(day.weekDate, 'YYYY-MM-DD').format('MMMM YYYY');
                monthsInTimesheet.add(monthYear);
            });
            
            // Add timesheet to each month it spans
            monthsInTimesheet.forEach(monthYear => {
                if (!acc[monthYear]) {
                    acc[monthYear] = [];
                }
                acc[monthYear].push(timesheet);
            });
            
            return acc;
        }, {});

        const sortedMonths = Object.keys(grouped).sort((a, b) => {
            return moment(b, 'MMMM YYYY') - moment(a, 'MMMM YYYY'); // Sort in descending order
        });
        
        const sortedGrouped = {};
        sortedMonths.forEach(month => {
            sortedGrouped[month] = grouped[month];
        });

        return sortedGrouped;
    };

    const renderTimesheetDetails = (timesheet) => {
        const dateHoursMap = {};
        const uniqueProjects = [];

        timesheet.timeSheetHours.forEach(timeSheetHour => {
            const formattedDate = timeSheetHour.weekDate;

            timeSheetHour.projectHours.forEach(projectHour => {
                if (!dateHoursMap[formattedDate]) {
                    dateHoursMap[formattedDate] = {};
                }
                if (!dateHoursMap[formattedDate][projectHour.projectName]) {
                    dateHoursMap[formattedDate][projectHour.projectName] = 0;
                }
                // Add project to uniqueProjects array if it doesn't exist
                if (!uniqueProjects.includes(projectHour.projectName)) {
                    uniqueProjects.push(projectHour.projectName);
                }
            });
        });

        // Update dateHoursMap with actual hours
        timesheet.timeSheetHours.forEach(timeSheetHour => {
            const formattedDate = timeSheetHour.weekDate;

            timeSheetHour.projectHours.forEach(projectHour => {
                dateHoursMap[formattedDate][projectHour.projectName] += projectHour.hours;
            });
        });

        // Adjust startDay to start from Monday (day 1)
        const startDay = moment(timesheet.weekStartDate, "YYYY-MM-DD").day(1).toDate();
        const days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date(startDay);
            date.setDate(startDay.getDate() + i);
            const formattedDate = date.toISOString().split('T')[0];
            return formattedDate;
        });

        // Calculate total hours per day across all projects
        const dailyTotals = days.map(date => {
            const totalForDay = uniqueProjects.reduce((acc, projectName) => {
                return acc + ((dateHoursMap[date] && dateHoursMap[date][projectName]) || 0);
            }, 0);
            return totalForDay;
        });

        // Render table rows
        const projectRows = uniqueProjects.map((projectName, index) => {
            const daysData = days.map((date, i) => {
                const hoursForDate = (dateHoursMap[date] && dateHoursMap[date][projectName]) || 0;
                return (
                    <td key={i}>
                        {hoursForDate.toFixed(2)}
                    </td>
                );
            });

            return (
                <tr key={index}>
                    <td>{projectName}</td>
                    {daysData}
                    <th>
                        <div className="days-view-total-hours">
                            {timesheet.timeSheetHours
                                .filter(timeSheetHour => timeSheetHour.projectHours.some(projectHour => projectHour.projectName === projectName))
                                .reduce((acc, timeSheetHour) => acc + timeSheetHour.projectHours.reduce((acc, ph) => ph.projectName === projectName ? acc + ph.hours : acc, 0), 0).toFixed(2)
                            }
                        </div>
                    </th>
                </tr>
            );
        });

        // Add total row for daily hours
        const totalRow = (
            <tr key="total-row">
                <td><strong>Total</strong></td>
                {dailyTotals.map((total, i) => (
                    <td key={i}>
                        <strong>{total.toFixed(2)}</strong>
                    </td>
                ))}
                <th>
                    <div className="days-view-total-hours">
                        <strong>{dailyTotals.reduce((acc, total) => acc + total, 0).toFixed(2)}</strong>
                    </div>
                </th>
            </tr>
        );

        return [...projectRows, totalRow];
    };

    const sendEmail = (email) => {
        if (!timesheets.length) {
            toast.warning("No data available for export.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        const exportData = timesheets.map((timesheet) => {
            const uniqueComments = getUniqueCommentsByDate(timesheet.timeSheetHours);
            const comments = uniqueComments
                .map(({ date, comment }) => `${moment(date).format("DD-MM-YYYY")}: ${comment}`)
                .join("; ");
            return {
                "Employee ID": timesheet.employeeId,
                "Employee Name": timesheet.employeeName,
                "Week Number": timesheet.weekNumber,
                "Start & End Date": `${timesheet.weekStartDate} - ${timesheet.weekEndDate}`,
                "Total (Hours)": timesheet.totalHours,
                Status: timesheet.status,
                Notes: comments,
            };
        });

        const ws = utils.json_to_sheet(exportData, {
            header: ["Employee ID", "Employee Name", "Week Number", "Start & End Date", "Total (Hours)", "Status", "Notes"]
        });

        ws['!cols'] = [
            { wpx: 100 },
            { wpx: 150 },
            { wpx: 100 },
            { wpx: 150 },
            { wpx: 100 },
            { wpx: 100 },
            { wpx: 300 },
        ];

        if (exportData.length > 0) {
            ['A1', 'B1', 'C1', 'D1', 'E1', 'F1', 'G1'].forEach(cell => {
                if (ws[cell]) {
                    ws[cell].s = { font: { bold: true } };
                }
            });
        }

        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Timesheets');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });

        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onloadend = () => {
            const link = document.createElement('a');
            link.href = reader.result;
            const today = new Date();
            const year = today.getFullYear().toString().slice(-2);
            const month = (today.getMonth() + 1).toString().padStart(2, '0');
            const day = today.getDate().toString().padStart(2, '0');
            const formattedDate = `${year}${month}${day}`;
            link.download = `WW360-Timesheet_Master_Report_${formattedDate}.xlsx`;

            link.click();

            const subject = "Timesheet List";
            const body = 'Please find the attached file.';
            const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

            toast.info('The Excel file has been generated and downloaded. Please attach the file manually to your email.', {
                position: toast.POSITION.TOP_CENTER,
            });

            setTimeout(() => {
                window.location.href = mailtoLink;
            }, 3000);
        };
    };

    const handleExport = () => {
        // Ensure you have data in state before exporting
        if (!timesheets.length) {
            toast.warning("No data available for export.", {
                position: toast.POSITION.TOP_CENTER,
            });
            return;
        }

        // Collect all unique dates from timesheetHours
        const allDates = Array.from(new Set(timesheets.flatMap(timesheet =>
            timesheet.timeSheetHours.map(day => moment(day.weekDate, "YYYY-MM-DD").format("DD-MM-YYYY"))
        )));

    // Add a Day column (weekday) next to Dates
    const headers = ["Employee ID", "Employee Name", "Week Number", "Start & End Date", "Total (Hours)", "Status", "Dates", "Day", "Hours", "Projects", "Notes"];

        // Transform the timesheet data
        const exportData = timesheets.flatMap(timesheet => {
            const uniqueComments = getUniqueCommentsByDate(timesheet.timeSheetHours);
            const commentMap = new Map(uniqueComments.map(({ date, comment }) => [moment(date).format("DD-MM-YYYY"), comment]));

            return allDates.flatMap(date => {
                const dayData = timesheet.timeSheetHours.find(day =>
                    moment(day.weekDate, "YYYY-MM-DD").format("DD-MM-YYYY") === date
                );

                if (!dayData || !dayData.projectHours.length) {
                    return [];
                }

                return dayData.projectHours.map(project => ({
                    "Employee ID": timesheet.employeeId,
                    "Employee Name": timesheet.employeeName,
                    "Week Number": timesheet.weekNumber,
                    "Start & End Date": `${timesheet.weekStartDate} - ${timesheet.weekEndDate}`,
                    "Total (Hours)": timesheet.totalHours,
                    "Status": timesheet.status,
                    // Parse date string (DD-MM-YYYY) into a real Date using moment
                    "Dates": moment(date, "DD-MM-YYYY").toDate(),
                    // Add weekday short name (e.g., Mon, Tue)
                    "Day": moment(date, "DD-MM-YYYY").format("ddd"),
                    "Hours": project.hours,
                    "Projects": project.projectName,
                    "Notes": commentMap.get(date) || ""
                }));
            }).filter(row => row.length !== 0);
        });


        // Create a worksheet from the transformed data
        const ws = utils.json_to_sheet(exportData, { header: headers });

        ws['!cols'] = headers.map(() => ({ wpx: 120 }));

        if (exportData.length > 0) {
            // Header styling for columns A..K (now includes Day as column H)
            ['A1','B1','C1','D1','E1','F1','G1','H1','I1','J1','K1'].forEach(cell => {
                if (ws[cell]) {
                    ws[cell].s = { font: { bold: true } };
                }
            });
        }

        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Timesheet');

        // Generate a buffer from the workbook
    // Ensure SheetJS treats Date objects as dates in the worksheet
    const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array', cellDates: true, dateNF: 'dd-mm-yyyy' });

        // Create a blob from the buffer and save the file
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
        const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
        const formattedDate = `${year}${month}${day}`;
        saveAs(data, `WW360-Timesheet_Master_Report_${formattedDate}.xlsx`);
    };

    const shareData = {
        title: 'Timesheet Report List',
        text: 'Check out the Timesheet Report List',
        url: window.location.href
    };

    const handleShare = async () => {
        try {
            await navigator.share(shareData);
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    useEffect(() => {
        const filterTimesheets = () => {
            const searchQueryLower = searchQuery.toLowerCase();
            const grouped = groupByMonth(timesheets);
            
            const filtered = Object.keys(grouped).reduce((acc, monthYear) => {
                const filteredTimesheets = grouped[monthYear].filter(timesheet => {
                    const employeeId = timesheet.employeeId ? timesheet.employeeId.toLowerCase() : '';
                    const employeeName = timesheet.employeeName ? timesheet.employeeName.toLowerCase() : '';
                    const weekNumber = timesheet.weekNumber ? String(timesheet.weekNumber).toLowerCase() : '';
                    const status = timesheet.status ? String(timesheet.status).toLowerCase() : '';

                    return (
                        employeeId.includes(searchQueryLower) ||
                        employeeName.includes(searchQueryLower) ||
                        status.includes(searchQueryLower) ||
                        weekNumber.includes(searchQueryLower)
                    );
                });

                if (filteredTimesheets.length > 0) {
                    acc[monthYear] = filteredTimesheets;
                }
                return acc;
            }, {});

            setFilteredGroupedTimesheets(filtered);
        };

        filterTimesheets();
    }, [searchQuery, timesheets]);

    const handleNotesClick = (timesheet) => {
        setSelectedTimesheet(timesheet);
        setShowNotesModal(true);
    };

    const closeNotesModal = () => {
        setShowNotesModal(false);
        setSelectedTimesheet(null);
    };

    return (
        <div>
            {loading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <ToastContainer />
            <div className="content-page">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="row mb-2">
                                <div className="col">
                                    <h3 className="title mb-2">Timesheet Report</h3>
                                </div>
                                <div className="col-auto">
                                    <a href={"/SubmitLeave"} className="btn btn-primary mt-lg-0 mt-4">
                                        <i className="ri-calendar-schedule-line"></i> Apply Leave
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="card mb-4">
                                <div className="card-body">
                                    <div className="row align-items-center justify-content-center">
                                        <div className="col-lg-5 col-md-8 col-sm-10">
                                            <div className="container-fluid row-rounded mb-0">
                                                <div className="row px-2 py-0">
                                                    <div className="input-group mb-0">
                                                        <input
                                                            type="search"
                                                            className="form-control search-input"
                                                            placeholder="Keyword search..."
                                                            value={searchQuery}
                                                            onChange={(e) => setSearchQuery(e.target.value)}
                                                        />
                                                        <div className="input-group-append">
                                                            <button className="btn btn-primary" type="button" title="Search" rel="tooltip">
                                                                <i className="ri-menu-search-line"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <small className="form-text text-muted text-center mt-0">
                                                <i className="ri-information-2-fill text-info"></i> Allows: Emp ID - Emp Name - Week Number - Status
                                            </small>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col">
                                            <h5 className="title mt-1 mb-0">Summary</h5>
                                        </div>
                                        <div className="col-auto">
                                            <button type="button" title="Email" className="btn btn-light btn-circle ml-2" onClick={() => sendEmail('')}>
                                                <i className="ri-mail-send-fill"></i>
                                            </button>
                                            <button type="button" title="Share" className="btn btn-light btn-circle ml-2" onClick={handleShare}>
                                                <i className="ri-share-fill"></i>
                                            </button>
                                            <button type="button" title="Export" className="btn btn-light btn-circle ml-2" onClick={handleExport}>
                                                <i className="ri-export-fill"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="row mt-3">
                                        <div className="col-md-12" id="timesheetDetailsSummary">
                                            {Object.keys(filteredGroupedTimesheets).length > 0 ? (
                                                Object.keys(filteredGroupedTimesheets).map((monthYear, index) => (
                                                    <div key={index}>
                                                        <h6 className="title accordion-title" data-toggle="collapse" data-target={`#monthDetails${index}`}>
                                                            <i className="ri-calendar-2-line"></i> {monthYear}
                                                            <span className="show-hours-details float-right">
                                                                <div className="row">
                                                                    <div className="col border-right"><i className="ri-time-line"></i> Hours</div>
                                                                    <div className="col-auto">
                                                                        <span className="text-success">
                                                                            <i className="ri-thumb-up-fill"></i>
                                                                            {filteredGroupedTimesheets[monthYear].reduce((total, timesheet) => 
                                                                                total + (timesheet.status?.toLowerCase() === 'approved' ? calculateHoursForMonth(timesheet, monthYear) : 0), 0).toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                    <div className="col-auto">
                                                                        <span className="text-secondary">
                                                                            <i className="ri-thumb-up-fill"></i>
                                                                            {filteredGroupedTimesheets[monthYear].reduce((total, timesheet) => 
                                                                                total + (timesheet.status?.toLowerCase() === 'submitted' ? calculateHoursForMonth(timesheet, monthYear) : 0), 0).toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                    <div className="col-auto">
                                                                        <span className="text-danger">
                                                                            <i className="ri-thumb-down-fill"></i>
                                                                            {filteredGroupedTimesheets[monthYear].reduce((total, timesheet) => 
                                                                                total + (timesheet.status?.toLowerCase() === 'rejected' ? calculateHoursForMonth(timesheet, monthYear) : 0), 0).toFixed(2)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </span>
                                                        </h6>
                                                        <div className="table-responsive timesheet-information collapse" id={`monthDetails${index}`} data-parent="#timesheetDetailsSummary">
                                                            <table className="table grid">
                                                                <thead className="bg-light">
                                                                    <tr>
                                                                        <th>Emp ID</th>
                                                                        <th>Emp Name</th>
                                                                        <th className="text-center">Week Number</th>
                                                                        <th>Start & End Date</th>
                                                                        <th className="text-center">Total <small>(Hours)</small></th>
                                                                        <th className="text-center">Status</th>
                                                                        <th className="text-center">Notes</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {filteredGroupedTimesheets[monthYear].map((timesheet, weekIndex) => (
                                                                        <React.Fragment key={weekIndex}>
                                                                            <tr data-toggle="collapse" data-target={`#weekDetails${index}-${weekIndex}`} className="accordion-row collapsed" title="More Details">
                                                                                <td><i className="ri-user-2-line"></i> {timesheet.employeeId}</td>
                                                                                <td>{timesheet.employeeName}</td>
                                                                                <td className="text-center"><span className="text-info border badge border-info">{timesheet.weekNumber}</span></td>
                                                                                <td>{moment(timesheet.weekStartDate, "YYYY-MM-DD").format("DD-MM-YYYY")} - {moment(timesheet.weekEndDate, "YYYY-MM-DD").format("DD-MM-YYYY")}</td>
                                                                                <td className="text-center"><span className="text-secondary">{calculateHoursForMonth(timesheet, monthYear).toFixed(2)}</span></td>
                                                                                <td className="text-center">
                                                                                    <span className={`badge border status ${timesheet.status?.toLowerCase() === 'approved' ? 'border-success text-success' : timesheet.status === 'Rejected' ? 'border-danger text-danger' : 'border-secondary text-secondary'}`}>
                                                                                        {timesheet.status?.toLowerCase() === 'rejected' ? (
                                                                                            <i className="ri-thumb-down-fill"></i>
                                                                                        ) : (
                                                                                            <i className="ri-thumb-up-fill"></i>
                                                                                        )}
                                                                                        {timesheet.status}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="text-center">
                                                                                    <button
                                                                                        className="btn btn-link text-info btn-action custom-dropdown-btn"
                                                                                        onClick={() => handleNotesClick(timesheet)}
                                                                                        title="View Notes"
                                                                                    >
                                                                                        <i className="ri-sticky-note-fill"></i>
                                                                                    </button>
                                                                                </td>
                                                                            </tr>
                                                                            <tr className="accordion">
                                                                                <td colSpan="7" className="px-0 collapse" id={`weekDetails${index}-${weekIndex}`} data-parent={`#monthDetails${index}`}>
                                                                                    <div className="accordion-content days-view section-submitted">
                                                                                        <table className="table grid">
                                                                                            <thead className="bg-light">
                                                                                                <tr>
                                                                                                    <th>Activity</th>
                                                                                                    <th>Sun <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(0).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Mon <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(1).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Tue <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(2).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Wed <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(3).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Thu <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(4).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Fri <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(5).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Sat <small>{moment(timesheet.weekStartDate, "YYYY-MM-DD").day(6).format('DD-MM-YYYY')}</small></th>
                                                                                                    <th>Total <small>(Hours)</small></th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {renderTimesheetDetails(timesheet)}
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        </React.Fragment>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                        {/* Add <hr> after each month's details */}
                                                        {index < Object.keys(filteredGroupedTimesheets).length - 1 && <hr />}
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="col">
                                                    <div className="no-data-found">
                                                        <i className="ri-calendar-fill"></i>
                                                        <div className="bounce ripple"><i className="ri-checkbox-circle-fill"></i></div>
                                                        <h6>No Timesheets Available</h6>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showNotesModal && selectedTimesheet && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Notes for {moment(selectedTimesheet.weekStartDate, "YYYY-MM-DD").format("DD-MM-YYYY")} to {moment(selectedTimesheet.weekEndDate, "YYYY-MM-DD").format("DD-MM-YYYY")}</h5>
                                <button type="button" className="close" onClick={closeNotesModal}>
                                    <span>×</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                {selectedTimesheet.timeSheetHours.some(day => day.projectHours.some(ph => ph.userComments)) ? (
                                    <ul className="list-group">
                                        {getUniqueCommentsByDate(selectedTimesheet.timeSheetHours).map((comment, idx) => (
                                            <li key={idx} className="list-group-item text-left">
                                                <strong>{moment(comment.date).format("DD-MM-YYYY")}</strong> - {comment.comment}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-center">No comments available for this timesheet.</p>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeNotesModal}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimesheetReport;